import { OptJobUnitSetting } from 'api/optJobs/optJobs.api'
import { OptProject, useOptProjectOptimizeMutation } from 'api/optProjects/optProjects.api'
import { useUnits } from 'api/units/units.api'
import { getSandboxOptType } from 'helpers/sandbox.helper/sandbox.helper'
import moment from 'moment'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField } from 'ui/atoms'
import { DigitalTwinAccumulatorLevels } from 'ui/components/DigitalTwinAccumulatorLevels/DigitalTwinAccumulatorLevels'
import { OptimizeButton } from 'ui/components/OptimizeFormProvider/components/OptimizeButton/OptimizeButton'
import { OptModelsAccumulatorLevels } from 'ui/components/OptModelAccumulatorLevels/OptModelsAccumulatorLevels'
import Datetime from 'utils/datetime/datetime'

type SandboxProjectHeaderProps = {
  project: OptProject
}

export default function SandboxProjectHeader({ project }: SandboxProjectHeaderProps): ReactElement {
  const { t } = useTranslation()
  const { mutateAsync, status: jobCreationStatus } = useOptProjectOptimizeMutation()

  const [isValid, setIsValid] = useState(true)
  const [unitSettings, setUnitSettings] = useState<Record<string, OptJobUnitSetting>>({})

  const digitalTwinUid = project.digital_twin
  const optModelId = project.opt_model

  const { accLevelStartTime, accLevelEndTime } = useMemo(() => {
    return {
      accLevelStartTime: project.start_time ? Datetime.toISOString(project.start_time) : undefined,
      accLevelEndTime: project.end_time ? Datetime.toISOString(project.end_time) : undefined,
    }
  }, [project.start_time, project.end_time])

  // Fetch units to determine if we should show OptModelsAccumulatorLevels
  const { data: units = [] } = useUnits({
    optModel: optModelId,
    unit_type: 'Storage',
    enabled: !digitalTwinUid && optModelId !== undefined,
  })

  const handleOptimize = useCallback(() => {
    mutateAsync({ id: project.id, inputData: { unit_settings: unitSettings } })
  }, [mutateAsync, project.id, unitSettings])

  const hasOptModelAccumulators = optModelId !== undefined && optModelId !== null && units.length > 0
  const hasDigitalTwinAccumulators = digitalTwinUid !== undefined && digitalTwinUid !== null

  return (
    <div className="bg-white px-7 pb-2 pt-2 space-x-3 flex flex-row flex-nowrap justify-between relative w-full">
      <div className="flex flex-col flex-1 justify-between space-y-2">
        <div className="flex flex-row justify-between align-top space-x-2">
          {/* Accumulator levels section */}
          {(hasOptModelAccumulators || hasDigitalTwinAccumulators) && (
            <div className="flex flex-row self-start">
              {hasOptModelAccumulators && (
                <OptModelsAccumulatorLevels
                  setIsValid={setIsValid}
                  setUnitSettings={setUnitSettings}
                  optModelId={optModelId}
                />
              )}
              {hasDigitalTwinAccumulators && (
                <DigitalTwinAccumulatorLevels
                  digitalTwinUid={digitalTwinUid}
                  optStartTime={accLevelStartTime}
                  optEndTime={accLevelEndTime}
                  sandboxProjectId={project.id}
                />
              )}
            </div>
          )}
        </div>

        {/* Sandbox info section */}
        <div className="flex flex-row gap-2">
          <TextField
            variant="outlined"
            disabled
            value={moment(project.start_time).format('YYYY-MM-DD HH:mm')}
            label={t('Start time')}
            aria-label={t('Start time')}
            margin="normal"
          />
          <TextField
            variant="outlined"
            disabled
            value={moment(project.end_time).format('YYYY-MM-DD HH:mm')}
            label={t('End time')}
            aria-label={t('End time')}
            margin="normal"
          />
          <TextField
            variant="outlined"
            label={t('Sandbox type')}
            disabled
            value={getSandboxOptType(project.project_type)}
            aria-label={t('Sandbox type')}
            margin="normal"
          />
        </div>
      </div>

      {/* Right-aligned section - contains optimization controls */}
      <div className="flex flex-col items-end m-0 p-0 gap-1">
        <OptimizeButton
          type="sandbox"
          onClick={handleOptimize}
          isValid={isValid}
          sandboxProjectId={project.id}
          creatingOptJob={jobCreationStatus === 'pending'}
          optimizeLabel={t('Optimize production plan for sandbox')}
        />
      </div>
    </div>
  )
}
